<script setup lang="ts">
import { SelectionModeEnum } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { SearchMode, useSearch } from "~/core/composables/useSearch";
import LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { OperationModelType } from "~/core/models/OperationModelType.enum";
import {
  Filters,
  type FilterSelectionValue,
  FiltersType,
} from "~/core/models/filters";
import { showNotificationError } from "~/core/utils/notifications";
import { CAMPAIGN_PARAMS_SCOPE } from "~/modules/campaign/constants/constants";
import { CampaignOMStatus } from "~/modules/campaign/models/CampaignOMStatus.enum";
import { useFetchDtwinModels } from "~/modules/dtwin/composables/useFetchDtwinModels";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinColumns } from "~/modules/dtwin/models/DtwinColumns.enum";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import type { Fleet } from "~/modules/fleet/models/Fleet.interface";
import { FleetColumns } from "~/modules/fleet/models/FleetColumns.enum";
import { FleetType } from "~/modules/fleet/models/FleetType.enum";
import fleetService from "~/modules/fleet/services/fleet.service";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

export interface CampaignFunnelStep2 {
  dtwins: Dtwin[];
  fleets: Fleet[];
}

const props = defineProps<{
  modelValue: CampaignFunnelStep2;
  operationModels: OperationModel[];
  operationModelType?: OperationModelType;
  fleetUid?: string;
  firmware?: Firmware;
  campaignStatus?: CampaignOMStatus;
}>();

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const emit = defineEmits(["update:modelValue", "update:resetSelected"]);

const {
  fetchAll: fetchAllDtwinModels,
  isLoading: isLoadingDtwinModels,
  results: resultsAllDtwinModels,
  error: errorDtwinModels,
} = useFetchDtwinModels({ organizationCode: currentOrganization.value?.code });

const initialSelectedDtwinsUid: Ref<string[]> = ref([]);

const {
  setFilter: setFilterDtwins,
  fetchData: fetchDtwins,
  isLoading: isLoadingDtwins,
  results: resultDtwins,
  error: errorDtwins,
} = useFetchDtwins({
  searchMode: SearchMode.FILTER_SEARCH,
  useQueryParametersForPagination: false,
  organizationCode: currentOrganization.value?.code,
});

const fleetsDevicesShow: Ref<boolean> = ref(false);
const infoFleet: Ref<Fleet | undefined> = ref();
const dtwinsColumns: DtwinColumns[] = [
  DtwinColumns.FRIENDLY_NAME,
  DtwinColumns.TYPE,
  DtwinColumns.SERIAL_NUMBER,
  DtwinColumns.SECTOR,
  DtwinColumns.CONNECTIVITY,
  DtwinColumns.METASTATUS,
  DtwinColumns.STATE,
  DtwinColumns.CREATED_AT,
  DtwinColumns.ACTION_IN_PROGRESS,
];

async function resetSelectedDtwins(fleetUid?: string) {
  if (fleetUid) {
    setFilterDtwins(Filters.DTWINS_IN_FLEET, fleetUid);

    const allSelectedDtwins = await fetchAllSelectedDtwins();

    if (!LxcError.check(allSelectedDtwins)) {
      initialSelectedDtwinsUid.value = allSelectedDtwins.map(
        (selectedDtwin) => selectedDtwin.uid,
      );
      selectedDtwins.value = allSelectedDtwins;
    }
  }
}

// Fetch all the selected dtwins recursively.
// This is required in order to set the selected dtwins in the list.
async function fetchAllSelectedDtwins(
  fromPage: number = 1,
  // only pageSizeMagicNumbers defined in useSearch are allowed
  pageSize: number = 50,
): Promise<Dtwin[] | LxcError> {
  const params = new Map();
  params.set(CAMPAIGN_PARAMS_SCOPE, true);
  await fetchDtwins(fromPage, pageSize, params);

  if (errorDtwins.value) {
    return errorDtwins.value;
  } else if (
    resultDtwins.value &&
    resultDtwins.value.pagination.count > 0 &&
    resultDtwins.value.pagination.page <
      Math.ceil(
        resultDtwins.value.pagination.totalCount /
          resultDtwins.value.pagination.pageSize,
      )
  ) {
    const dtwinsFetched = resultDtwins.value.results.slice();

    const dtwinsNextPageFetched: Dtwin[] | LxcError =
      await fetchAllSelectedDtwins(fromPage + 1, pageSize);
    if (LxcError.check(dtwinsNextPageFetched)) {
      return dtwinsNextPageFetched;
    } else {
      return dtwinsFetched.concat(dtwinsNextPageFetched);
    }
  } else {
    return resultDtwins.value?.results || [];
  }
}

const initialSelectedFleetsUid: Ref<string[]> = ref([]);
const isLoadingSelectedFleets = ref(false);
const errorSelectedFleets: Ref<LxcError | undefined> = ref(undefined);

async function resetSelectedFleets(fleetUid?: string) {
  if (fleetUid) {
    errorSelectedFleets.value = undefined;
    isLoadingSelectedFleets.value = true;

    const response = await fleetService.getOrganizationFleet({
      uid: fleetUid,
      organizationCode: currentOrganization.value?.code,
    });

    if (LxcError.check(response)) {
      errorSelectedFleets.value = response;
    } else if (response.subFleets) {
      initialSelectedFleetsUid.value = response.subFleets
        .map((subFleet) => subFleet.copyOf)
        .filter((fleetUid) => fleetUid !== undefined);
      selectedFleets.value = response.subFleets.map((subFleet) => {
        // reset the selected fleet
        // the only useful attributes are uid and hash, the others are not relevant
        return {
          uid: subFleet.copyOf,
          orgId: "",
          friendlyName: "",
          type: FleetType.STATIC,
          createdAt: "",
          hash: subFleet.hash,
        } as Fleet;
      });
    }

    isLoadingSelectedFleets.value = false;
  }
}

const form: ComputedRef<CampaignFunnelStep2> = computed({
  get() {
    return props.modelValue;
  },
  set(form: CampaignFunnelStep2) {
    emit("update:modelValue", form);
  },
});

const selectedDtwins = computed({
  get() {
    return form.value.dtwins;
  },
  set(selectedDtwins) {
    form.value.dtwins = selectedDtwins;
  },
});

const selectedFleets = computed({
  get() {
    return form.value.fleets;
  },
  set(selectedFleets) {
    form.value.fleets = selectedFleets;
  },
});

// compute model UID from firmware range
const modelUidFromFirmwareRange = computed(() => {
  let modelUidFromFirmwareRange: string | undefined = undefined;

  if (
    props.operationModelType === OperationModelType.FIRM_UPDATE &&
    props.firmware &&
    resultsAllDtwinModels.value
  ) {
    const model = resultsAllDtwinModels.value.find(
      (dtwinModel) => dtwinModel.name === props.firmware?.range,
    );
    modelUidFromFirmwareRange = model?.uid;
  }

  return modelUidFromFirmwareRange;
});

// compute filters to apply on dtwins list
const dtwinsListDefaultFilters = computed(() => {
  if (props.operationModelType) {
    const dtwinsListDefaultFilters: Map<Filters, FilterSelectionValue> =
      new Map([
        // reset filter on firmware declination
        [Filters.DTWIN_RANGE, []],

        // reset filter on firmware compatible software version
        [Filters.DTWIN_IN_SOFTWARE_VERSION, []],

        // reset filter on firmware compatible hardware version
        [Filters.DTWIN_IN_HARDWARE_VERSION, []],

        // filter on dtwins state
        [
          Filters.DTWINS_IN_LIFE_CYCLE_STATE,
          [DtwinLifeCycleState.PREPROVISIONED, DtwinLifeCycleState.REGISTERED],
        ],
      ]);

    // for all operations
    if (resultsAllDtwinModels.value) {
      // filter on types defined in the selected operation models
      let modelUids: string[] = [];
      for (const operationModel of props.operationModels) {
        if (operationModel.type === props.operationModelType) {
          for (const deviceType of operationModel.deviceTypes) {
            const models = resultsAllDtwinModels.value.filter(
              (dtwinModel) => dtwinModel.name === deviceType,
            );
            models.forEach((model) => {
              if (!modelUids.includes(model.uid)) {
                modelUids.push(model.uid);
              }
            });
          }
        }
      }

      // filter on dtwin models
      dtwinsListDefaultFilters.set(Filters.DTWIN_MODEL_TYPE, modelUids);
    }

    switch (props.operationModelType) {
      case OperationModelType.FIRM_UPDATE:
        {
          // filter on firmware range
          let modelUids: string[] = [];
          if (modelUidFromFirmwareRange.value) {
            modelUids = [modelUidFromFirmwareRange.value];
          }

          // filter on dtwin models
          dtwinsListDefaultFilters.set(Filters.DTWIN_MODEL_TYPE, modelUids);

          // filter on firmware declination
          if (props.firmware?.declination) {
            dtwinsListDefaultFilters.set(Filters.DTWIN_RANGE, [
              props.firmware?.declination,
            ]);
          } else {
            dtwinsListDefaultFilters.set(Filters.DTWIN_RANGE, []);
          }

          // filter on compatible firmware software version, do not filter if compatible software versions contain "*"
          dtwinsListDefaultFilters.set(
            Filters.DTWIN_IN_SOFTWARE_VERSION,
            props.firmware?.firmwareVersions &&
              !props.firmware?.firmwareVersions.includes("*")
              ? toRaw(props.firmware?.firmwareVersions)
              : [],
          );

          // filter on compatible firmware hardware version, do not filter if compatible hardware versions contain "*"
          dtwinsListDefaultFilters.set(
            Filters.DTWIN_IN_HARDWARE_VERSION,
            props.firmware?.hardwareVersions &&
              !props.firmware?.hardwareVersions.includes("*")
              ? toRaw(props.firmware?.hardwareVersions)
              : [],
          );
        }
        break;
      case OperationModelType.CONFIG:
        // no filter done on configuration type for now as the backend cannot not managed it because it is a attribute of the model
        break;
    }

    return dtwinsListDefaultFilters;
  }
});

const isValid: ComputedRef<boolean> = computed(() => {
  return selectedDtwins.value.length !== 0 || selectedFleets.value.length !== 0;
});

// build RSQL query for fleet compatibility criteria in order to get the count of compatible dtwins
const { appliedFilters } = useFetchDtwins({});
const { searchParams, setFilter } = useSearch(
  FiltersType.RSQL,
  appliedFilters,
  SearchMode.FILTER_SEARCH,
);
watch(
  () => dtwinsListDefaultFilters.value,
  () => {
    if (dtwinsListDefaultFilters.value) {
      for (let [filter, value] of dtwinsListDefaultFilters.value) {
        setFilter(filter, value);
      }
    }
  },
);

const onFleetRowClick = (fleet: Fleet) => {
  fleetsDevicesShow.value = true;
  infoFleet.value = fleet;
};

onMounted(async () => {
  await Promise.all([
    fetchAllDtwinModels(),
    resetSelectedDtwins(props.fleetUid),
    resetSelectedFleets(props.fleetUid),
  ]);
});

defineExpose({
  compatibilityCriteria: searchParams,
  initialSelectedDtwinsUid,
  initialSelectedFleetsUid,
  validate: () => {
    if (
      selectedDtwins.value.length === 0 &&
      selectedFleets.value.length === 0
    ) {
      const message = t("campaign.funnel.fleetSelection.validation.required");
      showNotificationError(message);
      return Promise.reject(message);
    } else {
      return Promise.resolve(true);
    }
  },
  isValid,
});
</script>

<template>
  <container-component
    class="!p-0"
    :is-loading="
      isLoadingDtwinModels || isLoadingDtwins || isLoadingSelectedFleets
    "
    :error="errorDtwinModels || errorDtwins || errorSelectedFleets"
  >
    <lxc-tabs :add-margin-bottom="false">
      <lxc-tab-pane :label="t('campaign.funnel.fleetSelection.devices')">
        <dtwin-list
          v-model:selected-dtwins="selectedDtwins"
          :row-click-selection-mode="SelectionModeEnum.FORCE"
          clickable
          :default-filters="dtwinsListDefaultFilters"
          :search-mode="SearchMode.FILTER_SEARCH"
          show-search-bar
          search-bar-class="mt-4 mb-5"
          :columns="dtwinsColumns"
          :disable-unselect="campaignStatus === CampaignOMStatus.RUNNING"
          show-quick-filter-on-selected-dtwins
          show-filters
          within-campaign-scope
        />
      </lxc-tab-pane>
      <lxc-tab-pane :label="t('campaign.funnel.fleetSelection.fleets')">
        <fleet-list
          v-model:selected-fleets="selectedFleets"
          :row-click-selection-mode="SelectionModeEnum.NO_SELECTION"
          :compatibility-criteria="searchParams as string"
          :columns="[
            FleetColumns.FRIENDLY_NAME,
            FleetColumns.DEVICE_TWIN_COUNT,
            FleetColumns.CREATED_AT,
            FleetColumns.MODIFIED_AT,
            FleetColumns.INCOMPATIBLE_DEVICE_TWIN_COUNT,
            FleetColumns.TOOLTIP,
          ]"
          search-bar-class="mt-4 mb-5"
          no-redirection
          :search-mode="SearchMode.FILTER_SEARCH"
          within-campaign-scope
          @row-click="onFleetRowClick"
        />
        <campaign-operation-mgr-funnel-step2-fleet-device-list
          v-model:show="fleetsDevicesShow"
          :fleet="infoFleet"
          :compatibility-criteria="searchParams as string"
        />
      </lxc-tab-pane>
    </lxc-tabs>
  </container-component>
</template>
